/*
 * Contact Section – WhatsApp CTA with real contact details
 * Botanical Wellness style – forest green background
 * Real contact: 054-6362929 | Sharongol1971@gmail.com | רחוב שוהם, קדם
 */

import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Phone, Mail, Instagram, MapPin, MessageCircle } from "lucide-react";

export default function ContactSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const handleWhatsAppClick = () => {
    const message = "היי שרון, אני רוצה להתאמן!";
    const whatsappUrl = `https://wa.me/9720546362929?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <section id="contact" className="relative py-20 md:py-32 bg-[#2D5A3D]">
      {/* Decorative top wave */}
      <div className="absolute top-0 left-0 right-0 -translate-y-[1px]">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none">
          <path d="M0,40 C360,0 720,80 1080,40 C1260,20 1380,60 1440,40 L1440,0 L0,0 Z" fill="#FFF8F0" />
        </svg>
      </div>

      <div className="container relative z-10" ref={ref}>
        {/* Section title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span
            className="text-[#E8A87C] text-sm font-semibold tracking-wider uppercase mb-3 block"
            style={{ fontFamily: "var(--font-body)" }}
          >
            מוכנה להתחיל?
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            בואי נתחיל
          </h2>
          <div className="w-20 h-1 bg-[#E8A87C] mx-auto mt-6 rounded-full" />
          <p
            className="text-[#B8D4C4] text-lg mt-6 max-w-xl mx-auto"
            style={{ fontFamily: "var(--font-body)" }}
          >
            צרי קשר ישירות דרך WhatsApp או דרך אחד מערוצי התקשורת שלי
          </p>
        </motion.div>

        {/* Main WhatsApp CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="max-w-2xl mx-auto mb-16"
        >
          <button
            onClick={handleWhatsAppClick}
            className="w-full flex items-center justify-center gap-4 bg-gradient-to-r from-[#25D366] to-[#20BA5A] hover:from-[#20BA5A] hover:to-[#1a9d4f] text-white font-bold text-xl md:text-2xl py-6 px-8 rounded-full transition-all duration-300 hover:shadow-2xl hover:shadow-green-500/30 transform hover:-translate-y-1"
            style={{ fontFamily: "var(--font-body)" }}
          >
            <MessageCircle className="w-8 h-8" />
            היי שרון, אני רוצה להתאמן!
          </button>
        </motion.div>

        {/* Contact info grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {/* Instagram */}
          <motion.a
            href="https://www.instagram.com/sharon1fit/"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="group flex flex-col items-center gap-4 p-6 bg-white/10 rounded-2xl hover:bg-gradient-to-br hover:from-[#E8A87C] hover:to-[#E07A5F] transition-all duration-300 hover:shadow-lg"
          >
            <div className="w-14 h-14 bg-white/20 group-hover:bg-white/30 rounded-full flex items-center justify-center transition-all">
              <Instagram className="w-7 h-7 text-[#E8A87C] group-hover:text-white transition-colors" />
            </div>
            <div className="text-center">
              <span className="text-[#B8D4C4] text-sm block" style={{ fontFamily: "var(--font-body)" }}>
                אינסטגרם
              </span>
              <span className="text-white text-lg font-semibold" style={{ fontFamily: "var(--font-body)" }}>
                @sharon1fit
              </span>
            </div>
          </motion.a>

          {/* Phone */}
          <motion.a
            href="tel:+9720546362929"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="group flex flex-col items-center gap-4 p-6 bg-white/10 rounded-2xl hover:bg-[#E8A87C] transition-all duration-300 hover:shadow-lg"
          >
            <div className="w-14 h-14 bg-white/20 group-hover:bg-white/30 rounded-full flex items-center justify-center transition-all">
              <Phone className="w-7 h-7 text-[#E8A87C] group-hover:text-white transition-colors" />
            </div>
            <div className="text-center">
              <span className="text-[#B8D4C4] text-sm block" style={{ fontFamily: "var(--font-body)" }}>
                טלפון
              </span>
              <span className="text-white text-lg font-semibold" dir="ltr" style={{ fontFamily: "var(--font-body)" }}>
                054-6362929
              </span>
            </div>
          </motion.a>

          {/* Email */}
          <motion.a
            href="mailto:Sharongol1971@gmail.com"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="group flex flex-col items-center gap-4 p-6 bg-white/10 rounded-2xl hover:bg-[#E8A87C] transition-all duration-300 hover:shadow-lg"
          >
            <div className="w-14 h-14 bg-white/20 group-hover:bg-white/30 rounded-full flex items-center justify-center transition-all">
              <Mail className="w-7 h-7 text-[#E8A87C] group-hover:text-white transition-colors" />
            </div>
            <div className="text-center">
              <span className="text-[#B8D4C4] text-sm block" style={{ fontFamily: "var(--font-body)" }}>
                אימייל
              </span>
              <span className="text-white text-sm font-semibold break-all" style={{ fontFamily: "var(--font-body)" }}>
                Sharongol1971@gmail.com
              </span>
            </div>
          </motion.a>

          {/* Location */}
          <motion.a
            href="https://maps.google.com/?q=רחוב+שוהם,+קדם,+ישראל"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.45, duration: 0.5 }}
            className="group flex flex-col items-center gap-4 p-6 bg-white/10 rounded-2xl hover:bg-[#E8A87C] transition-all duration-300 hover:shadow-lg"
          >
            <div className="w-14 h-14 bg-white/20 group-hover:bg-white/30 rounded-full flex items-center justify-center transition-all">
              <MapPin className="w-7 h-7 text-[#E8A87C] group-hover:text-white transition-colors" />
            </div>
            <div className="text-center">
              <span className="text-[#B8D4C4] text-sm block" style={{ fontFamily: "var(--font-body)" }}>
                כתובת
              </span>
              <span className="text-white text-base font-semibold" style={{ fontFamily: "var(--font-body)" }}>
                רח׳ שוהם, קדם
              </span>
            </div>
          </motion.a>
        </div>

        {/* Bottom text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="text-center text-[#B8D4C4] text-lg"
          style={{ fontFamily: "var(--font-body)" }}
        >
          אנחנו נחזור אלייך בהקדם ונתאים לך תוכנית אימון מושלמת
        </motion.p>
      </div>
    </section>
  );
}
